import { isStr } from 'lib/util'
import { get } from 'lib/sequence/component/state/value'
import { hide, show } from 'lib/sequence/component/state/hidden'

/**
 * Decides whether to show/hide `containerKey` based on the value state
 * of `fields`.
 *
 * Each element of `fields` can either be a string, in which case the `value`
 * state of the component represented by the string is investigated, or an
 * object like `{ [string]: fn }`, in which case the callback is executed with
 * the component represented by the key of the object.
 *
 * In either case, if all fields have no value, the container will be hidden.
 *
 * @example
 * ```js
 * displayContainerWithFields(
 *   'additionalInformationContainer',
 *   [
 *     'foo', // check get(foo)
 *     { bar: c => get(c)?.length === 0 } // check if get(foo) is empty list
 *   ]
 * )(module),
 * ```
 *
 * This is useful for detail screens on mobile where we want to hide a section
 * of information (label + box below) only if all the section's fields are
 * without value.
 *
 * @param {?string} containerKey
 * @param {(string | {[key: string]: (c: Gaia.Component.Spec) => boolean})[]} fields
 * @returns {function(): function(*, ...[*]): Promise<*>}
 */
export default (containerKey = null, fields = []) => () => async (components, ...args) => {
  if (!containerKey || fields.length === 0) {
    console.error('displayContainerWithFields called with invalid container and/or fields', containerKey, fields)
  }

  const { [containerKey]: container } = components

  const shouldHideContainer = fields
    .filter(p => !!p)
    .every((field) => {
      const isKey = isStr(field)

      const fieldName = isKey
        ? field
        : Object.keys(field)[0]
      const fieldComponent = components[fieldName]

      return isKey
        ? !get(fieldComponent)
        : field[fieldName](fieldComponent)
    })

  shouldHideContainer
    ? hide(container)
    : show(container)

  return [components, ...args]
}
